// Link-type grammar (#374). Requirement links are stored as ONE directional row
// `(req_id1, req_id2, link_type)` whose verb reads from req_id1's perspective —
// the opposite side is never stored. So the SAME stored verb has to be flipped
// depending on which end you are looking from, or a relationship reads backwards
// ("parent" where it should say "child", "derives from" where it should say the
// opposite). This module is the single place that knows the opposite of each verb
// and its human label, so the Relationships tab and the impact graph can never
// drift apart (the exact "fix one site, break another" risk called out in #374).
//
// Vocabulary mirrors the backend's authoritative sets in `core/coverage.py`
// (VERIFICATION/TRACEABILITY_LINK_TYPES) and the impact-tree direction classifier
// `core/requirements/impact_tree.py` (_DOWNSTREAM_OUTGOING_TYPES). It deliberately
// does NOT use the stale `src/models/link_types.py` map (unused, different pairs).

// Opposite (reciprocal) of each link verb. Symmetrical links map to themselves.
const OPPOSITE = {
  parent: 'child',
  child: 'parent',
  decomposes: 'decomposed_by',
  decomposed_by: 'decomposes',
  derives_from: 'derived_by',
  derived_by: 'derives_from',
  // #657: `derived_from`/`decomposed_into` are the SPELLINGS the add-relationship
  // dropdown (ItemDetails.js/LinkDialog.js) and the AI decompose flow
  // (useAiFunctions.js) actually store — distinct strings from the canonical
  // `derives_from`/`decomposes` above (the two vocabularies are NOT unified; see
  // linkGrammar.test.js and the module header). Without an entry here, neither
  // `oppositeLinkType` nor `humanizeLinkType` recognised them at all, so an
  // INCOMING `derived_from`/`decomposed_into` link fell through to the raw,
  // un-flipped fallback label — the same "reads backwards from the other end"
  // defect #374 fixed for `parent`/`child`, just for two different verbs that
  // #374's original pass didn't cover. Same reciprocal verbs as their canonical
  // siblings (`derived_by`/`decomposed_by`) since they mean the same relationship,
  // just spelled differently at the point of creation — deliberately NOT
  // renamed/migrated to the canonical spelling (no data touched, no other
  // consumer's stored value changes).
  derived_from: 'derived_by',
  decomposed_into: 'decomposed_by',
  refines: 'refined_by',
  refined_by: 'refines',
  satisfies: 'satisfied_by',
  satisfied_by: 'satisfies',
  allocated_to: 'allocates',
  allocates: 'allocated_to',
  implements: 'implemented_by',
  implemented_by: 'implements',
  depends_on: 'required_by',
  required_by: 'depends_on',
  verifies: 'verified_by',
  verified_by: 'verifies',
  validates: 'validated_by',
  validated_by: 'validates',
  tests: 'tested_by',
  tested_by: 'tests',
  // symmetrical / self-inverse
  related: 'related',
  related_to: 'related_to',
  relates_to: 'relates_to',
  traces_to: 'traces_to',
};

// Human-readable labels. Anything not listed falls back to a de-underscored,
// capitalised form (e.g. "some_link" -> "Some link").
const HUMAN = {
  parent: 'Parent',
  child: 'Child',
  decomposes: 'Decomposes',
  decomposed_by: 'Decomposed by',
  derives_from: 'Derives from',
  derived_by: 'Derived by',
  refines: 'Refines',
  refined_by: 'Refined by',
  satisfies: 'Satisfies',
  satisfied_by: 'Satisfied by',
  allocated_to: 'Allocated to',
  allocates: 'Allocates',
  implements: 'Implements',
  implemented_by: 'Implemented by',
  depends_on: 'Depends on',
  required_by: 'Required by',
  verifies: 'Verifies',
  verified_by: 'Verified by',
  validates: 'Validates',
  validated_by: 'Validated by',
  tests: 'Tests',
  tested_by: 'Tested by',
  related: 'Related',
  related_to: 'Related',
  relates_to: 'Related',
  traces_to: 'Traces to',
  // #657: explicit labels for the dropdown's own spellings (see the OPPOSITE
  // entries above) — the de-underscore fallback happens to produce the same
  // text, but spelling it out here keeps the pairing with `derived_by`/
  // `decomposed_by` visible in one place rather than relying on a coincidence.
  derived_from: 'Derived from',
  decomposed_into: 'Decomposed into',
};

// The verbs whose OUTGOING target is the DOWNSTREAM (lower, nearer-a-leaf) node —
// i.e. the verb already names the downstream side. Mirrors the backend
// `_DOWNSTREAM_OUTGOING_TYPES`. Everything else names the UPSTREAM side, so its
// opposite is the downstream-naming verb.
const DOWNSTREAM_NAMING = new Set([
  'child', 'satisfied_by', 'decomposed_by', 'derived_by', 'refined_by', 'allocates',
]);

function norm(linkType) {
  return String(linkType || 'related').toLowerCase();
}

/** The reciprocal verb (child<->parent, derives_from<->derived_by, …). Unknown
 * verbs (and symmetrical ones) return unchanged, so nothing is ever mislabelled
 * by guessing. */
export function oppositeLinkType(linkType) {
  const t = norm(linkType);
  return OPPOSITE[t] || t;
}

/** Human label for a raw link verb, with a safe de-underscored fallback. */
export function humanizeLinkType(linkType) {
  const t = norm(linkType);
  return HUMAN[t] || (t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' '));
}

/**
 * Label for a relationship as seen FROM the current item (the Relationships tab).
 * The stored verb reads from req_id1's side, so an INCOMING link (current item is
 * req_id2, the target) must be flipped to read correctly; an OUTGOING link is
 * already from our side. E.g. incoming `derives_from` → "Derived by" (they derive
 * from me); outgoing `parent` → "Parent" (the target is my parent).
 */
export function relationshipVerb(linkType, direction) {
  const verb = direction === 'incoming' ? oppositeLinkType(linkType) : norm(linkType);
  return humanizeLinkType(verb);
}

/**
 * Label for a lineage edge in the impact graph, which is always drawn top→bottom
 * (upstream above, downstream below). The edge should name the LOWER (downstream)
 * node's role, so we always resolve to the downstream-naming verb of the pair:
 * a downstream-naming stored verb is used as-is, an upstream-naming one is
 * flipped. E.g. `parent` → "Child", `derives_from` → "Derived by", `child` →
 * "Child", `decomposed_by` → "Decomposed by".
 */
export function lineageVerb(linkType) {
  const t = norm(linkType);
  const verb = DOWNSTREAM_NAMING.has(t) ? t : oppositeLinkType(t);
  return humanizeLinkType(verb);
}
